/**
 * CR-7732: which side of a call-off the person reading it is on.
 *
 * Company type is not the answer. A supplier a customer made its Logistics
 * Provider is the supplier on its own orders and the haulier on the loads it
 * carries for other suppliers — the same user, the same company type, two
 * different roles depending on which call-off is open. Asking the call-off
 * who its parties are is the only way to get that right.
 */
import type { CallOff } from '../../../lib/api';

export interface CallOffViewerRole {
  /** The viewer's company is this call-off's customer. */
  isCustomer: boolean;
  /** The viewer's company is this call-off's supplier. */
  isSupplier: boolean;
  /** The viewer carries this load and is party to neither side of the order. */
  isHaulier: boolean;
}

interface ViewerUser {
  company?: number | null;
  company_type?: string | null;
  platform?: string | null;
}

/**
 * The viewer's role on one call-off.
 *
 * @param callOff - the loaded call-off, or null while it is still fetching
 * @param user - the signed-in user, from AuthContext
 * @returns the three role flags; before the call-off loads, the user's own
 *   company type stands in so the first render is not blank
 *
 * @example
 * const { isCustomer, isHaulier } = resolveViewerRole(callOff, user);
 */
export function resolveViewerRole(
  callOff: CallOff | null | undefined,
  user: ViewerUser | null | undefined,
): CallOffViewerRole {
  const company = user?.company ?? null;

  if (!callOff || company === null) {
    // Fall back to the portal side while the call-off is in flight. A
    // logistics company is never a party, so it starts as the haulier.
    const type = user?.company_type;
    return {
      isCustomer: type === 'customer' || user?.platform === 'CUSTOMER',
      isSupplier: type === 'supplier' || user?.platform === 'SUPPLIER',
      isHaulier: type === 'logistics',
    };
  }

  const isCustomer = callOff.customer === company;
  const isSupplier = callOff.supplier === company;
  return {
    isCustomer,
    isSupplier,
    // Being assigned is not required to read it as a haulier: anyone who is
    // neither party got here by carrying it, and a load whose haulier was
    // just changed must not briefly render as a party's view.
    isHaulier: !isCustomer && !isSupplier,
  };
}
